import { useTranslation } from "react-i18next";
import { useState } from "react";
import { IconLink, IconCopy, IconCheck, IconRefresh } from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { Switch } from "@/components/lilia-ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/lilia-ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/lilia-ui/dialog";
import { useSharingApi } from "@/hooks/useSharingApi";
import {
  buildSharingError,
  classifyShareError,
  type SharingError,
  type SharingErrorAction,
} from "@/lib/sharing/errors";
import { SharingErrorBanner } from "../parts/SharingErrorBanner";

interface Props {
  documentId: string;
  isPublic: boolean;
  shareLink: string;
  shareSlug?: string;
  /** Iter 8 — initial server-side state. The component still
   *  carries local state to avoid drumroll-rate API calls per
   *  Select change, but flushes on every change to persist. */
  linkPermission?: string;
  linkExpiresAt?: string | null;
  onChange: (state: {
    isPublic: boolean;
    shareLink: string;
    shareSlug?: string;
    linkPermission?: string;
    linkExpiresAt?: string | null;
  }) => void;
}

// Spec §5 / §7: section-level switch in the header row (not a row
// buried in the body). When off, body collapses entirely — no
// "Generate link" button to misread. Two destructive actions —
// Regenerate (rotates slug) vs Revoke (turns the section off) —
// each gated by a confirm dialog with the consequence in present
// tense.
//
// `linkPermission` and `linkExpiresAt` controls render with
// defaults today (view / never); the spec marks the API extension
// (§12 row "API · link") as additive so we ship the UI now and
// wire persistence in a later iteration.
type LinkPermission = "view" | "comment";
type LinkExpiry = "never" | "24h" | "7d" | "30d";

const EXPIRIES: LinkExpiry[] = ["never", "24h", "7d", "30d"];

export function PublicLinkSection({
  documentId,
  isPublic,
  shareLink,
  shareSlug,
  linkPermission: initialPermission,
  linkExpiresAt: initialExpiresAt,
  onChange,
}: Props) {
  const { t } = useTranslation("sharing");
  const sharingApi = useSharingApi();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<SharingError | null>(null);
  const [linkPermission, setLinkPermission] = useState<LinkPermission>(
    (initialPermission as LinkPermission | undefined) ?? "view",
  );
  // Map persisted ISO expiresAt back to a coarse preset for the
  // Select. We compare against the *current* expected expiry of
  // each preset; anything that doesn't match a preset shows up as
  // "Never" (closest safe approximation — UI doesn't model
  // arbitrary dates yet).
  const [linkExpiry, setLinkExpiry] = useState<LinkExpiry>(() => {
    if (!initialExpiresAt) return "never";
    const days = Math.round(
      (new Date(initialExpiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
    );
    if (days <= 1) return "24h";
    if (days <= 7) return "7d";
    if (days <= 30) return "30d";
    return "never";
  });
  const [confirm, setConfirm] = useState<null | "regenerate" | "revoke">(null);

  // Compute the ISO expiresAt that maps to the picked preset.
  // "never" -> ClearExpiry; everything else -> now + duration.
  function presetToServerArgs(preset: LinkExpiry) {
    if (preset === "never") return { clearExpiry: true } as const;
    const hours = preset === "24h" ? 24 : preset === "7d" ? 24 * 7 : 24 * 30;
    const iso = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    return { expiresAt: iso } as const;
  }

  // Iter 8 — persist permission + expiry on every change. Calls
  // /share again with the new flags. Errors fall through the §6
  // banner so the user sees what happened.
  async function persistLinkOption(opts: { permission?: LinkPermission; expiry?: LinkExpiry }) {
    if (!isPublic) return; // No-op when link is off.
    setBusy(true);
    setError(null);
    try {
      const result = await sharingApi.enable(documentId, true, {
        permission: opts.permission ?? linkPermission,
        ...presetToServerArgs(opts.expiry ?? linkExpiry),
      });
      onChange({
        isPublic: true,
        shareLink: result.shareLink,
        shareSlug: result.shareSlug ?? "",
        linkPermission: result.linkPermission,
        linkExpiresAt: result.linkExpiresAt ?? null,
      });
    } catch (err) {
      setError(buildSharingError(classifyShareError(err, "link-generate-failed")));
    } finally {
      setBusy(false);
    }
  }

  function buildUrl() {
    if (!shareLink) return "";
    const path = shareSlug ? `${shareSlug}-${shareLink}` : shareLink;
    return `${window.location.origin}/shared/${path}`;
  }

  async function handleToggle() {
    setBusy(true);
    setError(null);
    try {
      if (isPublic) {
        // Spec §5: Revoke is destructive — confirm first instead
        // of toggling immediately. Reset the switch on cancel.
        setConfirm("revoke");
      } else {
        const result = await sharingApi.enable(documentId, true);
        onChange({ isPublic: true, shareLink: result.shareLink, shareSlug: result.shareSlug ?? "" });
      }
    } catch (err) {
      setError(buildSharingError(classifyShareError(err, "link-generate-failed")));
    } finally {
      setBusy(false);
    }
  }

  async function performRevoke() {
    setBusy(true);
    setError(null);
    try {
      await sharingApi.revoke(documentId);
      onChange({ isPublic: false, shareLink: "", shareSlug: "" });
    } catch (err) {
      setError(buildSharingError(classifyShareError(err)));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function performRegenerate() {
    // No dedicated regen endpoint yet; revoke + re-enable
    // approximates it and yields a fresh slug.
    setBusy(true);
    setError(null);
    try {
      await sharingApi.revoke(documentId);
      const result = await sharingApi.enable(documentId, true);
      onChange({ isPublic: true, shareLink: result.shareLink, shareSlug: result.shareSlug ?? "" });
    } catch (err) {
      setError(buildSharingError(classifyShareError(err, "link-generate-failed")));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function handleCopy() {
    const url = buildUrl();
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  function handleErrorAction(a: SharingErrorAction) {
    if (a.kind === "retry") void handleToggle();
    setError(null);
  }

  return (
    <section data-testid="share-link-section" data-public={isPublic ? "true" : "false"} className="py-5">
      <header className="mb-3 flex items-center gap-2">
        <IconLink size={16} className="text-[var(--lilia-text-secondary)]" />
        <h3 className="text-sm font-semibold text-[var(--lilia-text)]">{t("link.title")}</h3>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-[var(--lilia-text-muted)]">
          {isPublic ? t("link.on") : t("link.off")}
          <Switch data-testid="share-link-toggle" checked={isPublic} onCheckedChange={handleToggle} disabled={busy} />
        </span>
      </header>

      {error && (
        <div className="mb-3">
          <SharingErrorBanner
            error={error}
            onAction={handleErrorAction}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {!isPublic ? (
        <p className="text-xs text-[var(--lilia-text-muted)]">
          {t("link.inactive")}
        </p>
      ) : (
        <div className="space-y-3">
          {/* URL + copy */}
          <div className="flex items-center gap-2">
            <input
              data-testid="share-link-url"
              type="text"
              readOnly
              value={buildUrl()}
              className="min-w-0 flex-1 truncate rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-bg)] px-3 py-2 text-xs text-[var(--lilia-text-muted)] outline-none"
            />
            <Button data-testid="share-link-copy" variant="outline" size="icon-sm" onClick={handleCopy} title={t("link.copy")}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </Button>
          </div>

          {/* Permission + expiry — view defaults; comment reserved
              ("soon"). Per §5, no "Can edit" via public link. */}
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1">
              <span className="block text-xs font-medium text-[var(--lilia-text-muted)]">
                {t("link.permission")}
              </span>
              <Select
                value={linkPermission}
                onValueChange={(v) => {
                  const next = v as LinkPermission;
                  setLinkPermission(next);
                  void persistLinkOption({ permission: next });
                }}
              >
                <SelectTrigger data-testid="share-link-permission" className="h-9 w-full text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">{t("roles.viewer")}</SelectItem>
                  <SelectItem value="comment" disabled>
                    {t("roles.comment")}
                    <span className="ml-2 rounded bg-[var(--lilia-bg)] px-1.5 py-0.5 text-[10px] text-[var(--lilia-text-muted)]">
                      {t("soon")}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium text-[var(--lilia-text-muted)]">
                {t("link.expires")}
              </span>
              <Select
                value={linkExpiry}
                onValueChange={(v) => {
                  const next = v as LinkExpiry;
                  setLinkExpiry(next);
                  void persistLinkOption({ expiry: next });
                }}
              >
                <SelectTrigger data-testid="share-link-expiry" className="h-9 w-full text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRIES.map((k) => (
                    <SelectItem key={k} value={k} data-testid="share-link-expiry-option" data-expiry={k}>
                      {t(`link.expiry.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <p className="text-xs text-[var(--lilia-text-muted)]">
            {t(`link.${linkPermission === "comment" ? "summaryComment" : "summaryView"}.${linkExpiry}`)}
          </p>

          {/* Regenerate · Revoke — text-link affordances, both
              gated by confirm. Spec §5 keeps them deliberately
              separated so users can rotate without losing the
              section entirely. */}
          <div className="flex items-center gap-3 pt-1 text-xs">
            <button
              type="button"
              data-testid="share-link-regenerate"
              className="inline-flex items-center gap-1 text-[var(--lilia-text-muted)] underline-offset-2 hover:text-[var(--lilia-text)] hover:underline"
              onClick={() => setConfirm("regenerate")}
              disabled={busy}
            >
              <IconRefresh size={12} />
              {t("link.regenerate")}
            </button>
            <span className="text-[var(--lilia-border)]">·</span>
            <button
              type="button"
              data-testid="share-link-revoke"
              className="text-red-600 underline-offset-2 hover:underline dark:text-red-400"
              onClick={() => setConfirm("revoke")}
              disabled={busy}
            >
              {t("link.revoke")}
            </button>
          </div>
        </div>
      )}

      <Dialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="share-link-confirm-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirm === "regenerate" ? t("link.confirmRegenerate") : t("link.confirmRevoke")}
            </DialogTitle>
            <DialogDescription>
              {confirm === "regenerate"
                ? t("link.regenerateNote")
                : t("link.revokeNote")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="share-link-confirm-cancel" variant="outline" size="sm" onClick={() => setConfirm(null)}>
              {t("link.cancel")}
            </Button>
            <Button
              data-testid="share-link-confirm"
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => (confirm === "regenerate" ? performRegenerate() : performRevoke())}
            >
              {confirm === "regenerate" ? t("link.regenerate") : t("link.turnOff")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
